/* eslint-disable max-lines */
import constate from "constate";

import {
  isFinalVerificationMobileVariant1,
  isFinalVerificationMobileVariant2,
  shouldShowAiStudio as shouldShowAiStudioUtil,
  shouldShowDesktopFinalVerification,
  shouldShowDiverseSurfaces,
  shouldShowNegativeSpaceTool,
  shouldShowWeaveDesignLab
} from "./experiments";
import { DEFAULT_FREE_SHIPPING_THRESHOLD } from "../constants/constants";

export type DesignLabConfig = {
  flip?: {
    paidExpertEditAddon?: boolean;
    madeSamplesV1?: boolean;
    designLabCommunityArt?: boolean;
    cachedDeliveryDateAvailable?: boolean;
    manualBgRemovalBrushSize?: boolean;
    weaveAiImageRefinement?: boolean;
    editToolsAiImageRefinement?: boolean;
    dtfWorkerEnhancement?: boolean;
    dtfBackendTriggeredEnhancement?: boolean;
    designLabNewEnhancementTrigger?: boolean;
    customerReviewOnPrintRedraws?: boolean;
    customerReviewOnRedraws?: boolean;
  };
  experiments?: {
    abSmartRedrawNotes?: number;
    abSkipPdpVectorization?: number;
    abShippingEnhancementServices?: number;
    abWeaverAiStudio?: number;
    abWeaveDesignLab2?: number;
    abDiverseSurfaces?: number;
    abDesignLabFinalVerification?: number;
    abDesignLabNegativeSpaceTool?: number;
    abDesignLabReorderTabs?: number;
  };
  is_sample?: boolean;
  isSample?: boolean;
  ai_design?: boolean;
  aiDesign?: boolean;
  aiCreditsBalance?: number;
  cachedEstimatedDeliveryDate?: string;
  orderNumber?: string;
  fbData?: string;
  supportPhoneNumber: string;
  showLocalDeliveryToggle?: boolean;
  localDeliveryAddressEligible?: boolean;
  defaultCompositionId: string;
  isStaff?: boolean;
  freeShippingThreshold?: number;
  isFreeShippingThresholdZero?: boolean;
};

export type ConfigProviderProps = {
  config: DesignLabConfig;
};

function useDesignLabConfigCreator({ config }: ConfigProviderProps) {
  /**
   * Determines if the Artist Edit addon should be shown based on the paidExpertEditAddon experiment
   * @returns true if Artist Edit should be shown, false if it should be hidden
   */
  const shouldShowArtistEditAddon = config.flip?.paidExpertEditAddon;

  const shouldShowSmartRedrawNotes = config.experiments?.abSmartRedrawNotes === 1;

  /**
   * Determines if the addon price should be shown based on the paidExpertEditAddon experiment
   * @returns true if addon price should be shown, false if it should be hidden
   */
  const shouldShowExpertTab = !config.flip?.paidExpertEditAddon;

  const shouldShowArtistInstructions = shouldShowSmartRedrawNotes;

  /**
   * Determines if AI Studio button should be shown based on the abWeaverAiStudio experiment
   * Variant 1 (50%) shows the button, variant 0 (50%) is control
   * @returns true if AI Studio button should be shown, false if it should be hidden
   */
  const shouldShowAiStudio = shouldShowAiStudioUtil(config.experiments?.abWeaverAiStudio);

  /**
   * Determines if Weave Design Lab flow should be shown based on the abWeaveDesignLab experiment
   * Variant 1 (candidate) shows the new weave design flow, variant 0 (control) shows the existing upload flow
   * @returns true if Weave Design Lab should be shown, false if Upload Artwork should be used
   */
  const shouldShowWeaveDesignLabFeature = shouldShowWeaveDesignLab(
    config.experiments?.abWeaveDesignLab2
  );

  /**
   * Determines if diverse surfaces (model selection) should be shown based on the abDiverseSurfaces experiment
   * Variant 1 (candidate) shows the model selection feature, variant 0 (control) hides it
   * @returns true if diverse surfaces should be shown, false if it should be hidden
   */
  const shouldShowDiverseSurfacesFeature = shouldShowDiverseSurfaces(
    config.experiments?.abDiverseSurfaces
  );

  const shouldExperiments = config.experiments;

  // Support both snake_case and camelCase versions
  const isSample = config.isSample || config.is_sample || false;
  const aiDesign = config.aiDesign || config.ai_design || false;
  const isStaff = config.isStaff || false;

  const shouldShowCommunityArt = config.flip?.designLabCommunityArt || false;

  const shouldShowCachedDeliveryDate = config.flip?.cachedDeliveryDateAvailable || false;
  const { cachedEstimatedDeliveryDate } = config;
  const shouldShowManualBgRemovalBrushSize = config.flip?.manualBgRemovalBrushSize || false;
  const shouldShowNegativeSpaceAutomation = shouldShowNegativeSpaceTool(
    config.experiments?.abDesignLabNegativeSpaceTool
  );

  const shouldShowDesktopFinalVerificationFeature = shouldShowDesktopFinalVerification(
    config.experiments?.abDesignLabFinalVerification
  );

  const shouldShowFinalVerificationMobileVariant1 = isFinalVerificationMobileVariant1(
    config.experiments?.abDesignLabFinalVerification
  );

  const shouldShowFinalVerificationMobileVariant2 = isFinalVerificationMobileVariant2(
    config.experiments?.abDesignLabFinalVerification
  );

  const flip = (config.flip ?? {}) as Record<string, boolean>;
  const shouldShowWeaveAiImageRefinement = flip["weaveAiImageRefinement"] ?? false;
  const shouldShowEditToolsAiImageRefinement = flip["editToolsAiImageRefinement"] ?? false;
  const { supportPhoneNumber, defaultCompositionId } = config;
  const dtfWorkerEnhancement = flip["dtfWorkerEnhancement"] ?? false;
  const dtfBackendTriggeredEnhancement = flip["dtfBackendTriggeredEnhancement"] ?? false;
  const showLocalDeliveryToggle = config.showLocalDeliveryToggle ?? false;
  const localDeliveryAddressEligible = config.localDeliveryAddressEligible ?? false;
  const shouldUseDesignLabNewEnhancementTrigger = flip["designLabNewEnhancementTrigger"] ?? false;
  const shouldShowCustomerReviewOnPrintRedraws = flip["customerReviewOnPrintRedraws"] ?? false;
  const shouldShowCustomerReviewOnRedraws = flip["customerReviewOnRedraws"] ?? false;
  const freeShippingThreshold = config.freeShippingThreshold ?? DEFAULT_FREE_SHIPPING_THRESHOLD;
  const isFreeShippingThresholdZero = config.isFreeShippingThresholdZero ?? false;

  return {
    config,
    shouldShowArtistEditAddon,
    shouldShowExpertTab,
    shouldShowSmartRedrawNotes,
    shouldShowArtistInstructions,
    shouldShowAiStudio,
    shouldShowWeaveDesignLabFeature,
    shouldShowDiverseSurfacesFeature,
    shouldExperiments,
    isSample,
    aiDesign,
    isStaff,
    shouldShowCommunityArt,
    shouldShowCachedDeliveryDate,
    cachedEstimatedDeliveryDate,
    shouldShowManualBgRemovalBrushSize,
    shouldShowNegativeSpaceAutomation,
    shouldShowDesktopFinalVerificationFeature,
    shouldShowFinalVerificationMobileVariant1,
    shouldShowFinalVerificationMobileVariant2,
    shouldShowWeaveAiImageRefinement,
    shouldShowEditToolsAiImageRefinement,
    supportPhoneNumber,
    defaultCompositionId,
    dtfWorkerEnhancement,
    dtfBackendTriggeredEnhancement,
    showLocalDeliveryToggle,
    localDeliveryAddressEligible,
    shouldUseDesignLabNewEnhancementTrigger,
    shouldShowCustomerReviewOnPrintRedraws,
    shouldShowCustomerReviewOnRedraws,
    freeShippingThreshold,
    isFreeShippingThresholdZero
  };
}

export const [
  ConfigProvider,
  useDesignLabConfig,
  useDtfBackendTriggeredEnhancement,
  useDtfWorkerEnhancement,
  useShowArtistEditAddon,
  useShowExpertTab,
  useShowSmartRedrawNotes,
  useShowArtistInstructions,
  useShowAiStudio,
  useShowDiverseSurfaces,
  useExperiments,
  useIsSample,
  useShowWeaveDesignLab,
  useAiDesign,
  useIsStaff,
  useShowCommunityArt,
  useShowCachedDeliveryDate,
  useCachedEstimatedDeliveryDate,
  useShowManualBgRemovalBrushSize,
  useShowNegativeSpaceAutomation,
  useShowDesktopFinalVerification,
  useShowFinalVerificationMobileVariant1,
  useShowFinalVerificationMobileVariant2,
  useShowWeaveAiImageRefinement,
  useShowEditToolsAiImageRefinement,
  useSupportPhoneNumber,
  useShowLocalDeliveryToggle,
  useLocalDeliveryAddressEligible,
  useDefaultCompositionId,
  useDesignLabNewEnhancementTrigger,
  useFreeShippingThreshold,
  useIsFreeShippingThresholdZero
] = constate(
  useDesignLabConfigCreator,
  value => value.config,
  value => value.dtfBackendTriggeredEnhancement,
  value => value.dtfWorkerEnhancement,
  value => value.shouldShowArtistEditAddon,
  value => value.shouldShowExpertTab,
  value => value.shouldShowSmartRedrawNotes,
  value => value.shouldShowArtistInstructions,
  value => value.shouldShowAiStudio,
  value => value.shouldShowDiverseSurfacesFeature,
  value => value.shouldExperiments,
  value => value.isSample,
  value => value.shouldShowWeaveDesignLabFeature,
  value => value.aiDesign,
  value => value.isStaff,
  value => value.shouldShowCommunityArt,
  value => value.shouldShowCachedDeliveryDate,
  value => value.cachedEstimatedDeliveryDate,
  value => value.shouldShowManualBgRemovalBrushSize,
  value => value.shouldShowNegativeSpaceAutomation,
  value => value.shouldShowDesktopFinalVerificationFeature,
  value => value.shouldShowFinalVerificationMobileVariant1,
  value => value.shouldShowFinalVerificationMobileVariant2,
  value => value.shouldShowWeaveAiImageRefinement,
  value => value.shouldShowEditToolsAiImageRefinement,
  value => value.supportPhoneNumber,
  value => value.showLocalDeliveryToggle,
  value => value.localDeliveryAddressEligible,
  value => value.defaultCompositionId,
  value => value.shouldUseDesignLabNewEnhancementTrigger,
  value => value.freeShippingThreshold,
  value => value.isFreeShippingThresholdZero
);
